import {
  Box,
  Button,
  ConfirmationModal,
  Divider,
  Flex,
  Heading,
  Layout,
  Text,
  Toast,
  Tooltip,
  useConfirmModal,
} from '@modusign/design-system';
import { ChangeEvent, FocusEvent, ReactText, useCallback, useRef, useState } from 'react';

import fileHashBackground from '@app/assets/file-hash-background.svg';

import getPDFSelectorErrorMessage from '../../libs/getPDFSelectorErrorMessage';
import isValidDocumentIdFormat from '../../libs/isValidDocumentIdFormat';
import FileSelectErrorCode from '../../types/FileSelectErrorCode';
import DocumentIDInput from '../DocumentIDInput';
import InputWrapper from '../InputWrapper';
import PDFSelector from '../PDFSelector';

const getDocumentIDErrorMessage = ({
  documentId,
  isInvalid,
  hadInteracted,
}: {
  documentId: string | null;
  isInvalid: boolean;
  hadInteracted: boolean;
}) => {
  if (!documentId && hadInteracted) {
    return '문서 ID 입력은 필수입니다.';
  }

  if (isInvalid && hadInteracted) {
    return '올바르지 않은 ID 형식입니다.';
  }

  return null;
};

interface FileHashDocumentUploadProps {
  isSubmitting: boolean;
  onClickOpenHelpModal: () => void;
  onSubmit: (documentId: string, file: File) => void;
}

export default function FileHashDocumentUpload({
  isSubmitting,
  onClickOpenHelpModal,
  onSubmit,
}: FileHashDocumentUploadProps) {
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [hadInteracted, setHasInteracted] = useState(false);
  const isValidDocumentId = isValidDocumentIdFormat(documentId);
  const [selectedFile, setSelectedFile] = useState<File>();
  const isFileSelected = !!selectedFile;
  const toastIdsRef = useRef<Set<ReactText>>(new Set());
  const { modalProps: fileReselectModalProps, openModal: openFileReselectModal } =
    useConfirmModal();

  const handleDocumentIdChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setHasInteracted(true);
    setDocumentId(e.target.value);
  }, []);

  const handleDocumentIdFocusOut = (e: FocusEvent<HTMLInputElement>) => {
    setHasInteracted(true);
    if (e.target.value) {
      setDocumentId(e.target.value.replace(/\s/g, ''));
    }
  };

  const resetDocumentId = useCallback(() => {
    setDocumentId(null);
    setHasInteracted(false);
  }, []);

  const closeToasts = useCallback(() => {
    if (toastIdsRef.current.size > 0) {
      toastIdsRef.current.forEach((toast) => Toast.close(toast));
      toastIdsRef.current.clear();
    }
  }, []);

  const setSelectedFileAndShowToast = useCallback((file: File | undefined) => {
    setSelectedFile(file);
    if (file) {
      toastIdsRef.current.add(
        Toast.success({
          description: '업로드할 파일을 선택했습니다.',
        })
      );
    }
  }, []);

  const handleFileSelect = useCallback(
    async (file: File | undefined) => {
      if (selectedFile && file) {
        closeToasts();
        if (await openFileReselectModal()) {
          setSelectedFileAndShowToast(file);
        }
      } else {
        setSelectedFileAndShowToast(file);
      }
    },
    [closeToasts, openFileReselectModal, selectedFile, setSelectedFileAndShowToast]
  );

  const handleFileSelectError = useCallback((errorCode: FileSelectErrorCode) => {
    toastIdsRef.current.add(
      Toast.error({
        description: getPDFSelectorErrorMessage(errorCode),
      })
    );
  }, []);

  const handleSubmit = useCallback(() => {
    if (!!documentId && !!selectedFile) {
      closeToasts();
      onSubmit(documentId, selectedFile);
    }
  }, [documentId, onSubmit, selectedFile, closeToasts]);

  const handleOpenHelpModal = useCallback(() => {
    closeToasts();
    onClickOpenHelpModal();
  }, [closeToasts, onClickOpenHelpModal]);

  const documentIDErrorMessage = getDocumentIDErrorMessage({
    documentId,
    hadInteracted,
    isInvalid: !isValidDocumentId,
  });

  return (
    <>
      <ConfirmationModal
        {...fileReselectModalProps}
        confirmButtonLabel="교체"
        maxWidth="xxs"
        title="파일 교체"
        variant="warning"
      >
        <Box>
          <Text as="p" textAlign="center">
            선택한 파일로 문서를 교체할까요?
          </Text>
        </Box>
      </ConfirmationModal>
      <Flex
        backgroundColor="bodyNeutralBase"
        height="100%"
        justifyContent="center"
        sx={{
          backgroundImage: `url(${fileHashBackground})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'max(1920px,100%)',
          backgroundPositionX: 'center',
        }}
        width="100%"
      >
        <Layout flex={1} flexDirection="column" maxWidth="xs" sx={{ boxSizing: 'content-box' }}>
          <Box mt={10} pb={9} textAlign="center">
            <Heading fontSize={32} fontWeight="medium" mb={4}>
              계약, 체결 후에도 안전하게
            </Heading>
            <Text as="h3" color="textNeutralSlightlyLight" fontSize="lg" fontWeight="regular">
              모두싸인으로 체결된 문서가 원본이 맞는지 걱정하셨나요?
              <br />
              원본 검증키 대조로 문서의 위·변조 여부를 누구나 쉽게 확인할 수 있습니다.
            </Text>
          </Box>
          <InputWrapper
            checked={isValidDocumentId}
            linkName="문서 ID 확인 방법"
            title="문서 ID"
            onLinkNameClick={handleOpenHelpModal}
          >
            <DocumentIDInput
              errorMessage={documentIDErrorMessage}
              height={70}
              value={documentId}
              onBlur={handleDocumentIdFocusOut}
              onChange={handleDocumentIdChange}
              onClearClick={resetDocumentId}
            />
          </InputWrapper>
          <Divider mb={9} mt={4} />
          <InputWrapper checked={isFileSelected} mt={7} title="검증 대상 문서">
            <PDFSelector
              selectedFile={selectedFile}
              onError={handleFileSelectError}
              onFileSelect={handleFileSelect}
            />
            <Text
              color="textNeutralVeryLight"
              fontSize="xs"
              fontWeight="regular"
              mt={4}
              textAlign="center"
            >
              안심하세요.
              <br />
              업로드된 파일은 오직 문서 검증에만 사용하며, 다른 용도로 저장하거나 활용하지 않습니다.
            </Text>
          </InputWrapper>
          <Flex justifyContent="center" m={10}>
            <Tooltip
              content="문서 ID 입력 후 파일을 선택해 주세요."
              disabled={isValidDocumentId && isFileSelected}
              placement="top"
            >
              <Button
                className="tagDocumentVerificationBtn"
                disabled={!isValidDocumentId || !isFileSelected}
                height={48}
                isLoading={isSubmitting}
                variant="primary"
                width={180}
                onClick={handleSubmit}
              >
                문서 검증하기
              </Button>
            </Tooltip>
          </Flex>
        </Layout>
      </Flex>
    </>
  );
}
